'use client';

import { FC, ReactNode, useMemo } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { logEventAsArray, TAnalyticArgs } from '@/analytics';
import { Card, TIconName } from '@mofid-securities/uikit';
import { Icon } from '@/components/atomic/Icon';
import {
  IUserStatusOnClickWrapperProps,
  UserStatusMaybeButtonOrLink,
} from '@/userStatus/components';
import { useVariant } from '@/unleash';
import { useIsMofidCardDisabled } from '@/hooks/isMofidCardDisabled';
import { classnames } from '@/utils/classnames';
import { SuggestionMenu } from './SuggestionMenu';

export interface IBottomNavigationItem {
  id?: string;
  title: string;
  href: string | undefined;
  badge?: ReactNode;
  icon: TIconName;
  activeIcon: TIconName;
  checkActive: (
    __item: Omit<IBottomNavigationItem, 'checkActive'>
  ) => boolean | undefined;
  analytics?: TAnalyticArgs;
  isHidden?: boolean;
  forbiddenStatuses?: IUserStatusOnClickWrapperProps['forbiddenStatuses'];
}

const useBottomNavigationItems = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { isMofidCardDisabled } = useIsMofidCardDisabled();

  return useMemo(() => {
    const items: IBottomNavigationItem[] = [
      {
        id: 'navbar_click_home',
        title: 'خانه',
        href: '/',
        icon: 'home_outline',
        activeIcon: 'home',
        checkActive: (item) =>
          pathname === item.href && searchParams?.get('index') !== '1',
        analytics: ['NAVBAR_HOME'],
      },
      {
        title: 'کارت',
        href: '/?index=1',
        icon: 'mofid_card_outline',
        id: 'navbar_click_card',
        analytics: ['navbar_click_card'],
        activeIcon: 'mofid_card',
        isHidden: isMofidCardDisabled,
        checkActive: () =>
          (pathname === '/' && searchParams?.get('index') === '1') ||
          pathname?.startsWith('/card'),
      },
      {
        id: 'navbar_click_funds',
        title: 'صندوق‌های مفید',
        href: '/invest/fund',
        icon: 'investment_outline',
        activeIcon: 'investment',
        checkActive: (item) =>
          pathname?.startsWith(`${item?.href}`) ||
          pathname?.startsWith(`/invest/etf`),
        analytics: ['NAVBAR_FUNDS'],
      },
      {
        id: 'navbar_click_PFM',
        title: 'مدیریت مالی',
        href: '/pfm',
        icon: 'pfm_outline',
        activeIcon: 'pfm',
        checkActive: (item) => pathname?.startsWith(`${item?.href}`),
        analytics: ['NAVBAR_PFM'],
        forbiddenStatuses: ['GUEST', 'ANONYMOUS'],
      },
    ];

    return items;
  }, [pathname, searchParams, isMofidCardDisabled]);
};

export interface IBottomNavigationProps {}

const BottomNavigation: FC<IBottomNavigationProps> = () => {
  const { enabled: isSuggestionMenuEnabled } = useVariant(
    'web.general.suggestionMenu'
  );

  const handleLinkClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    item: IBottomNavigationItem
  ) => {
    logEventAsArray(item?.analytics);
    if (!item.href) e.preventDefault();
  };

  const items = useBottomNavigationItems();

  return (
    <Card
      className="fixed inset-x-0 bottom-0 z-40 rounded-t-3xl
                 border border-gray-10 shadow-[0_-4px_20px_rgba(0,0,0,0.05)] "
      padding={0}
    >
      <SuggestionMenu />
      <nav
        className={classnames(
          'h-20 rounded-2xl pb-[7px] pt-[9px]',
          !isSuggestionMenuEnabled && 'shadow-[0_-4px_20px_rgba(0,0,0,0.05)]'
        )}
      >
        <ul className="flex h-full flex-row items-center justify-around">
          {items.map((item: IBottomNavigationItem) => {
            const isActive: boolean = Boolean(item.checkActive(item));
            return (
              !item?.isHidden && (
                <li key={item.title} className="flex-1">
                  <UserStatusMaybeButtonOrLink
                    forbiddenStatuses={item?.forbiddenStatuses}
                    href={item?.href}
                  >
                    <div
                      className={`flex flex-1 flex-col items-center ${
                        isActive
                          ? 'text-blue-50'
                          : 'text-gray-filled-tertiary-enabled'
                      }`}
                      id={item.id}
                      onClick={(e) => handleLinkClick(e, item)}
                    >
                      <Icon
                        icon={isActive ? item.activeIcon : item.icon}
                        size={28}
                      />
                      <span
                        className={`relative mt-2 text-center text-xxs font-medium leading-3 ${
                          isActive && 'font-demibold'
                        }`}
                      >
                        {item.title}
                        {item.badge || null}
                      </span>
                    </div>
                  </UserStatusMaybeButtonOrLink>
                </li>
              )
            );
          })}
        </ul>
      </nav>
    </Card>
  );
};

export default BottomNavigation;
